import React from 'react';

import type { GameState } from '../../types/game';
import { isVerboseLoggingEnabled, logger } from '../../utils/logger';

const FALLBACK_POLL_DELAYS_MS = [1500, 2500, 4000] as const;
const FALLBACK_HEARTBEAT_GRACE_MS = 4500;

function classifyOnlineRoute(pathname: string, search: string): 'lobby' | 'in-game' | 'game-over' | 'results' | 'other' {
  if (!pathname.startsWith('/online')) return 'other';
  if ((pathname === '/online' || pathname === '/online/') && new URLSearchParams(search).get('view') === 'results') {
    return 'game-over';
  }
  if (pathname.startsWith('/online/game/')) return 'in-game';
  if (pathname.startsWith('/online/game-over')) return 'game-over';
  if (pathname.startsWith('/online/results/')) return 'results';
  if (pathname === '/online' || pathname === '/online/') return 'lobby';
  return 'other';
}

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return String(error);
};

export function useOnlineFallbackPoll({
  gameState,
  isHost,
  lobbyPendingReadyChanges,
  refreshSnapshot,
  requestAutoRefresh,
  syncLog,
  lastOnlineEventAtRef,
  lastOnlineStatusRef,
}: {
  gameState: GameState;
  isHost: boolean;
  lobbyPendingReadyChanges: Set<string>;
  refreshSnapshot: (reason: string) => Promise<boolean>;
  requestAutoRefresh: (reason: 'visibility' | 'focus' | 'app-resume') => void;
  syncLog: (label: string, payload: Record<string, unknown>) => void;
  lastOnlineEventAtRef: React.RefObject<number>;
  lastOnlineStatusRef: React.RefObject<string | null>;
}) {
  const hasTurnCycle = Boolean(gameState.turnCycle);

  React.useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    if (gameState.mode === 'REAL' || !gameState.id) return;
    const routeKind = classifyOnlineRoute(window.location.pathname, window.location.search);
    const isLobbyRoute = routeKind === 'lobby';
    const isActiveGameRoute = routeKind === 'in-game';
    if (!isLobbyRoute && !isActiveGameRoute) {
      syncLog('FALLBACK_POLL_SKIP_ROUTE', {
        gameId: gameState.id,
        phase: gameState.lobby?.phase || null,
        routeKind,
      });
      return;
    }

    const isLobbyPhase = ['LOBBY', 'ORDER_REVEAL'].includes(gameState.lobby?.phase || '');
    const isActiveGamePhase = gameState.lobby?.phase === 'IN_GAME' || hasTurnCycle;
    if (!isLobbyPhase && !(isActiveGamePhase && isActiveGameRoute)) return;

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        requestAutoRefresh('visibility');
      }
    };

    const onFocus = () => {
      if (document.visibilityState !== 'visible') return;
      requestAutoRefresh('focus');
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [gameState.id, gameState.lobby?.phase, gameState.mode, hasTurnCycle, requestAutoRefresh, syncLog]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (gameState.mode === 'REAL' || !gameState.id) return;
    const routeKind = classifyOnlineRoute(window.location.pathname, window.location.search);
    const isLobbyRoute = routeKind === 'lobby';
    const isActiveGameRoute = routeKind === 'in-game';
    if (!isLobbyRoute && !isActiveGameRoute) {
      syncLog('FALLBACK_POLL_SKIP_ROUTE', {
        gameId: gameState.id,
        phase: gameState.lobby?.phase || null,
        routeKind,
      });
      return;
    }
    const phase = gameState.lobby?.phase || null;
    const isLobbyPhase = ['LOBBY', 'ORDER_REVEAL'].includes(gameState.lobby?.phase || '');
    const isInGamePhase = gameState.lobby?.phase === 'IN_GAME' || hasTurnCycle;
    if (!isLobbyPhase && !(isInGamePhase && isActiveGameRoute)) return;

    let cancelled = false;
    let fallbackEnabled = false;
    let fallbackStartedAtMs = 0;
    let backoffIndex = 0;
    let timerId: number | null = null;
    const pollDelays = [...FALLBACK_POLL_DELAYS_MS];
    const healthCheckMs = FALLBACK_POLL_DELAYS_MS[0];
    const staleLobbyEventThresholdMs = 6000;

    const setFallback = (enabled: boolean, reason: string) => {
      if (enabled === fallbackEnabled) return;
      fallbackEnabled = enabled;
      if (enabled) {
        fallbackStartedAtMs = Date.now();
        syncLog('FALLBACK_POLL_ON', {
          gameId: gameState.id,
          phase,
          isHost,
          mode: isInGamePhase ? 'in-game' : 'lobby',
          reason,
          backoffMs: pollDelays[backoffIndex],
        });
        return;
      }
      syncLog('FALLBACK_POLL_OFF', {
        gameId: gameState.id,
        phase,
        isHost,
        mode: isInGamePhase ? 'in-game' : 'lobby',
        reason,
      });
      fallbackStartedAtMs = 0;
      backoffIndex = 0;
    };

    const assessOnlineHealth = (): { degraded: boolean; reason: string } => {
      const status = lastOnlineStatusRef.current;
      if (!status) return { degraded: false, reason: 'status-unknown' };
      if (status !== 'SUBSCRIBED') return { degraded: true, reason: `status-${status.toLowerCase()}` };
      if (fallbackEnabled) {
        const heartbeatAt = lastOnlineEventAtRef.current;
        const heartbeatRecovered = heartbeatAt > 0 && heartbeatAt >= fallbackStartedAtMs;
        const fallbackElapsedMs = Date.now() - fallbackStartedAtMs;
        if (!heartbeatRecovered && fallbackElapsedMs < FALLBACK_HEARTBEAT_GRACE_MS) {
          return { degraded: true, reason: `awaiting-heartbeat-${fallbackElapsedMs}` };
        }
      }
      if (isLobbyPhase && lobbyPendingReadyChanges.size > 0 && lastOnlineEventAtRef.current > 0) {
        const silenceMs = Date.now() - lastOnlineEventAtRef.current;
        if (silenceMs > staleLobbyEventThresholdMs) {
          return { degraded: true, reason: `event-silence-${silenceMs}` };
        }
      }
      return { degraded: false, reason: 'healthy' };
    };

    const refreshFromSnapshot = async () => {
      try {
        if (cancelled) return;
        await refreshSnapshot('fallback-poll');
      } catch (error) {
        if (isVerboseLoggingEnabled()) {
          logger.warn('LOBBY_SNAPSHOT_REFRESH_FAILED', { error: getErrorMessage(error) });
        }
      }
    };

    const scheduleNext = (delayMs: number) => {
      if (cancelled) return;
      if (timerId != null) window.clearTimeout(timerId);
      timerId = window.setTimeout(() => {
        void runCycle();
      }, delayMs);
    };

    const runCycle = async () => {
      if (cancelled) return;
      const health = assessOnlineHealth();
      if (!health.degraded) {
        setFallback(false, health.reason);
        scheduleNext(healthCheckMs);
        return;
      }

      setFallback(true, health.reason);
      await refreshFromSnapshot();
      backoffIndex = Math.min(backoffIndex + 1, pollDelays.length - 1);
      scheduleNext(pollDelays[backoffIndex]);
    };

    void runCycle();
    return () => {
      cancelled = true;
      if (timerId != null) window.clearTimeout(timerId);
      setFallback(false, 'effect-cleanup');
    };
  }, [gameState.id, gameState.lobby?.phase, gameState.mode, hasTurnCycle, isHost, lobbyPendingReadyChanges, refreshSnapshot, syncLog, lastOnlineEventAtRef, lastOnlineStatusRef]);
}
